/** Must stay the first import: it adds `crypto.randomUUID` on non-secure origins before any other module runs. */
import './random-uuid-polyfill.js';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

import { initAxe } from './app/a11y.js';
import AuthGate from './app/gates/AuthGate.js';
import { MaintenanceGate } from './app/gates/MaintenanceGate.js';
import TeamsOnlyGate from './app/gates/TeamsOnlyGate.js';
import { AppProviders } from './app/providers.js';
import { App } from './App.js';
import { initDensity } from './features/settings/density-store.js';
import { initializeIdentity, refreshIdentity } from './identity.js';
import { registerTeamsLifecycle } from './integration/runtime/teams-lifecycle.js';
import { logger } from './logger.js';
import { onAppForeground } from './lib/app-presence.js';
import { loadRuntimeConfig } from './lib/runtime-config.js';
import { isPublicViewPath } from './lib/share-path.js';
import { isTeamsAppSuspended } from './lib/teams-suspension.js';
import { getClientOperationQueue } from './queue/client-operation-queue.js';
import { setIdentity, setOnline } from './store/identity-store.js';
// `setOnline` is exported for online/offline event listeners only.
import { captureInstallPrompt } from './features/pwa-install/install-prompt.js';
import { followPushClicks } from './features/web-push/push-click-listener.js';
import { registerServiceWorker } from './sw/register.js';
import { applyResolvedTheme } from './ui/theme/theme-store.js';
import './styles/globals.css';

async function bootstrap(): Promise<void> {
  const rootEl = document.getElementById('root');
  if (rootEl === null) {
    throw new Error('boot: #root element not found in index.html');
  }

  // Apply the stored theme attributes before the first paint so there's no
  // flash of unthemed content on cold reload.
  applyResolvedTheme();
  initDensity();
  if (!isPublicViewPath(window.location.pathname)) captureInstallPrompt();

  // Pull runtime config (SSO client, admin-frame, section/connector visibility,
  // maintenance) from the backend before first paint, so the SPA carries no
  // build-time identity. This does delay the first render by the fetch, but it is
  // bounded: loadRuntimeConfig races a timeout and falls back to safe defaults, so
  // a slow/down /v1/config delays boot by at most that timeout rather than hanging.
  await loadRuntimeConfig();

  const root = createRoot(rootEl);

  /**
   * Public view bypass. `/share/*` (chat snapshots) and `/shared/*` (a file handed beyond its
   * workspace) are the chrome-free, read-only routes: an external or not-yet-signed-in viewer must be
   * able to load them, so we render the router WITHOUT the AuthGate/TeamsOnlyGate/MaintenanceGate walls
   * and skip the identity + Teams-SDK bootstrap entirely. Those routes read any existing platform JWT
   * directly from the store; they never force a login.
   */
  const isPublicShareView = isPublicViewPath(window.location.pathname);

  // Determine connectivity by polling /v1/health rather than navigator.onLine.
  // navigator.onLine reflects OS-level network, not whether the server is reachable.
  const HEALTH_URL = '/health';
  const HEALTH_INTERVAL_MS = 15_000;

  /**
   * Polls the health endpoint and records whether the server answered. Skipped while Teams keeps the
   * tab suspended: nothing is on screen, so polling would only spend the user's battery.
   */
  async function checkHealth(): Promise<void> {
    if (isTeamsAppSuspended()) return;
    try {
      const res = await fetch(HEALTH_URL, { method: 'GET', cache: 'no-store' });
      setOnline(res.ok);
    } catch {
      setOnline(false);
    }
  }

  // The share viewer stands alone (static snapshot): skip connectivity polling + online/offline
  // wiring entirely, or every public viewer would run a needless /health poll every 15s.
  if (!isPublicShareView) {
    void checkHealth();
    setInterval(() => void checkHealth(), HEALTH_INTERVAL_MS);

    // Keep navigator.onLine as a fast-path signal for immediate offline detection.
    window.addEventListener('offline', () => setOnline(false));
    window.addEventListener('online', () => void checkHealth());
  }

  // Render a minimal splash first so the tab is never blank, even while
  // we resolve identity. The Shell itself subscribes to the store.
  root.render(
    <StrictMode>
      {isPublicShareView ? (
        <AppProviders>
          <App />
        </AppProviders>
      ) : (
        <TeamsOnlyGate>
          <MaintenanceGate>
            <AuthGate>
              <AppProviders>
                <App />
              </AppProviders>
            </AuthGate>
          </MaintenanceGate>
        </TeamsOnlyGate>
      )}
    </StrictMode>,
  );

  // The share viewer stands alone — no identity resolution, no Teams SDK, no
  // focus-refresh. It handles its own (optional) bearer per request, so bailing
  // here keeps an unauthenticated viewer off every gated bootstrap path.
  if (isPublicShareView) {
    registerServiceWorker();
    return;
  }

  void registerTeamsLifecycle();

  // Kick off identity resolution. The promise flows through the module
  // singleton so future callers (e.g. API clients) reuse the same value.
  //
  // We deliberately do NOT call setOnline(false) on identity failure — that
  // conflates "backend rejected our token" / "no /v1/me yet" with "the
  // browser is offline". The only signal for online/offline is the browser's
  // own `navigator.onLine` (wired above + via the online/offline events).
  try {
    const identity = await initializeIdentity();
    setIdentity(identity.me, identity.stale);
    // Persist userId so the legacy x-user-id chat path (teams.ts) uses the
    // real DB user ID instead of the anon-* fallback, which Bedrock rejects.
    localStorage.setItem('ct-user-id', identity.me.userId);
    logger.info(
      `Identity resolved: ${identity.me.displayName} (${identity.me.tenantId})${
        identity.stale ? ' [stale]' : ''
      }`,
    );
  } catch (err: unknown) {
    logger.error('Failed to resolve identity and no cache available', err);
    // Still flip `loaded=true` so identity-aware gates (MaintenanceGate
    // included) stop waiting on a resolution that will never come.
    setIdentity(null);
  }

  // Prime the client operation queue singleton so the queue counter in the
  // sidebar has something to subscribe to from first paint.
  getClientOperationQueue();

  refreshIdentityWhenAppReturns();

  registerServiceWorker();
  if ('serviceWorker' in navigator) followPushClicks();

  // Dev-only a11y audit. Production tree-shakes this away.
  void initAxe();
}

/**
 * Re-fetches `/v1/me` each time the app comes back: a visible page again, or Teams resuming a
 * suspended tab. The embedded admin panel can flip `prefs.showAdminLink` while the user is away, and
 * this updates the sidebar without a manual reload.
 */
function refreshIdentityWhenAppReturns(): void {
  onAppForeground(() => {
    void refreshIdentity()
      .then((next) => setIdentity(next.me, next.stale))
      .catch((err: unknown) => {
        logger.debug('refreshIdentity on focus failed', err);
      });
  });
}

// bootstrap() returns a Promise; `void` makes the no-await call explicit.
void bootstrap();
